import * as React from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';

import { Money } from '../../../../types';
import Gateway from '../Gateway';
import { useTheme } from '../../../../theme';
import { PaymentPage, PaymentType } from '../../../types';
import Button from './Button/Button';

import PaymentsClient = google.payments.api.PaymentsClient;
import IsReadyToPayRequest = google.payments.api.IsReadyToPayRequest;
import CardPaymentMethod = google.payments.api.CardPaymentMethod;
import AllowedAuthMethod = google.payments.api.AllowedAuthMethod;
import AllowedCardNetwork = google.payments.api.AllowedCardNetwork;
import PaymentMethodTokenizationSpecification = google.payments.api.PaymentMethodTokenizationSpecification;
import PaymentDataRequest = google.payments.api.PaymentDataRequest;
import TransactionInfo = google.payments.api.TransactionInfo;
import MerchantInfo = google.payments.api.MerchantInfo;

const allowedCardNetworks: AllowedCardNetwork[] = ['MASTERCARD', 'VISA'];
const allowedCardAuthMethods: AllowedAuthMethod[] = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];
const baseCardPaymentMethod: CardPaymentMethod = {
	type: 'CARD',
	parameters: {
		allowedAuthMethods: allowedCardAuthMethods,
		allowedCardNetworks: allowedCardNetworks
	}
};

const googlePayBaseConfiguration: IsReadyToPayRequest = {
	apiVersion: 2,
	apiVersionMinor: 0,
	allowedPaymentMethods: [baseCardPaymentMethod]
};

const tokenizationSpecification: PaymentMethodTokenizationSpecification = {
	type: 'PAYMENT_GATEWAY',
	parameters: {
		gateway: 'example',
		gatewayMerchantId: 'gatewayMerchantId'
	}
};

const cardPaymentMethod: CardPaymentMethod = {
	type: 'CARD',
	tokenizationSpecification: tokenizationSpecification,
	parameters: {
		allowedCardNetworks: allowedCardNetworks,
		allowedAuthMethods: allowedCardAuthMethods,
		billingAddressRequired: true,
		billingAddressParameters: {
			format: 'FULL',
			phoneNumberRequired: true
		}
	}
};

const merchantInfo: MerchantInfo = {
	merchantId: '01234567890123456789',
	merchantName: 'Example Merchant Name'
};

interface Props {
	price: Money;
	onClick: (page: PaymentPage) => void;
	onSuccess?: () => void;
	onFail?: () => void;
	isPreOpened?: boolean;
}

const GooglePay = memo<Props>(({ price, onClick, onSuccess, onFail, isPreOpened }) => {
	const { GooglePay: css } = useTheme('PaymentForm');
	const { t } = useTranslation('PaymentForm');
	const [button, setButton] = useState<HTMLElement>(null);
	const [googlePayClient, setGooglePayClient] = useState<PaymentsClient>(null);
	const [isUnavailable, setIsUnavailable] = useState(false);

	const handleOnClick = useCallback(() => {
		onClick({
			type: PaymentType.Gpay,
			title: 'Google Pay',
			description: t('In the next step you will be taken to a Google Pay page.'),
			content: <Button button={button} />
		});
	}, [onClick, button]);

	// For some strange reason, Helment+script onLoad requires global function name.
	useEffect(() => {
		window['onGooglePayError'] = () => {
			setIsUnavailable(true);
		};

		window['onGooglePayLoaded'] = () => {
			try {
				const googlePayClient = new google.payments.api.PaymentsClient({
					environment: 'TEST'
				});

				setGooglePayClient(googlePayClient);
			} catch (err) {
				console.error(err);
				setIsUnavailable(true);
			}
		};

		return () => {
			delete window['onGooglePayError'];
			delete window['onGooglePayLoaded'];
		};
	}, []);

	const onPaymentClick = async () => {
		const transactionInfo: TransactionInfo = {
			totalPriceStatus: 'FINAL',
			totalPrice: price.amount.toString(10),
			currencyCode: price.currency
		};

		const paymentDataRequest: PaymentDataRequest = {
			...googlePayBaseConfiguration,
			allowedPaymentMethods: [cardPaymentMethod],
			transactionInfo: transactionInfo,
			merchantInfo: merchantInfo
		};

		try {
			const paymentData = await googlePayClient.loadPaymentData(paymentDataRequest);

			if (onSuccess) {
				onSuccess();
			}

			console.log(paymentData);
		} catch (err) {
			console.error(err);

			if (onFail) {
				onFail();
			}
		}
	};

	const checkIfGooglePayAvailable = async () => {
		try {
			const response = await googlePayClient.isReadyToPay(googlePayBaseConfiguration);

			if (response.result) {
				const button = googlePayClient.createButton({
					buttonColor: 'default',
					buttonType: 'long',
					onClick: onPaymentClick
				});

				setButton(button);
			} else {
				setIsUnavailable(true);
			}
		} catch (err) {
			console.error('Error determining readiness to use Google Pay: ', err);
			setIsUnavailable(true);
		}
	};

	useEffect(() => {
		if (googlePayClient) {
			checkIfGooglePayAvailable();
		}
	}, [googlePayClient]);

	useEffect(() => {
		if (isPreOpened && button) {
			handleOnClick();
		}
	}, [isPreOpened, button]);

	useEffect(() => {
		if (isPreOpened && isUnavailable) {
			onClick({
				type: PaymentType.Gpay,
				title: 'Google Pay',
				content: null
			});
		}
	}, [isPreOpened, isUnavailable, onClick]);

	return (
		<>
			<Helmet async>
				<script
					src="https://pay.google.com/gp/p/js/pay.js"
					onLoad={'onGooglePayLoaded()' as any}
					onError={'onGooglePayError()' as any}
				/>
			</Helmet>

			{button && (
				<Gateway
					onClick={handleOnClick}
					header={
						<img
							src="https://cdn.websky.aero/content/frontend/images/payment/google-pay.svg"
							className={css.logo}
							alt="Google Pay"
						/>
					}
					type="Google Pay"
					paymentType={PaymentType.Gpay}
				/>
			)}
		</>
	);
});

export default GooglePay;
